import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShoppingBag, Search, ShoppingCart, Package, Tag, Wrench, Utensils } from "lucide-react";
import { ProductCard } from "@/components/marketplace/shared";
import { useTour } from "@/components/Onboarding";

export default function Marketplace() {
  useTour("buyer_marketplace");
  const { user } = useAuth();
  const [hasShop, setHasShop] = useState(false);
  useEffect(() => { if (!user) { setHasShop(false); return; } api.get("/sellers/me").then(r => setHasShop(!!r.data?.id)).catch(() => setHasShop(false)); }, [user]);
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const [q, setQ] = useState("");
  const [cats, setCats] = useState([]);
  const [items, setItems] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  useEffect(() => { api.get("/marketplace/categories", { params: { kind: "products" } }).then(r => setCats(r.data)); }, []);
  useEffect(() => {
    if (user) api.get("/cart").then(r => setCartCount(r.data.items_count)).catch(() => {});
  }, [user]);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/products", { params: { ...(category && { category }), ...(q && { q }), ...(verifiedOnly && { verified_only: true }) } }).then(r => setItems(r.data));
    }, 200);
    return () => clearTimeout(t);
  }, [category, q, verifiedOnly]);

  const setCat = (c) => { const p = new URLSearchParams(params); c ? p.set("category", c) : p.delete("category"); setParams(p); };

  return (
    <div className="page fade-up">
      <div className="pt-4 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShoppingBag className="text-[#c8102e]" size={22} />
          <h1 className="font-display text-2xl font-extrabold">Marketplace</h1>
        </div>
        <Link to="/marketplace/carrinho" data-testid="mp-cart-link" className="relative p-2 rounded-full border border-zinc-200 hover:border-black">
          <ShoppingCart size={18} />
          {cartCount > 0 && <span data-testid="mp-cart-count" className="absolute -top-1 -right-1 bg-[#c8102e] text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{cartCount}</span>}
        </Link>
      </div>

      <div className="grid grid-cols-6 gap-2 mb-4 text-center">
        <Quick to="/marketplace" icon={Tag} label="Produtos" testid="mp-nav-products" active />
        <Quick to="/marketplace/servicos" icon={Wrench} label="Serviços" testid="mp-nav-services" />
        <Quick to="/restaurantes" icon={Utensils} label="Restaurantes" testid="mp-nav-restaurants" />
        <Quick to="/marketplace/compras" icon={Package} label="Compras" testid="mp-nav-orders" />
        <Quick to="/vender" icon={ShoppingBag} label={hasShop ? "Minha loja" : "Vender"} testid="mp-nav-sell" highlight />
      </div>

      <div className="relative mb-3">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input data-testid="mp-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Procurar produtos…"
               className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-zinc-200 bg-white text-sm outline-none focus:border-black" />
      </div>
      <div className="flex overflow-x-auto gap-2 pb-2 -mx-4 px-4 scrollbar-none mb-3">
        <button onClick={() => setCat("")} className="chip whitespace-nowrap" data-active={!category}>Todas</button>
        <button data-testid="mp-filter-verified" onClick={() => setVerifiedOnly(v => !v)} className="chip whitespace-nowrap" data-active={verifiedOnly}>✓ Só verificadas</button>
        {cats.map(c => <button key={c.id} onClick={() => setCat(c.name)} data-testid={`mp-cat-${c.id}`} className="chip whitespace-nowrap" data-active={category === c.name}>{c.name}</button>)}
      </div>

      {items === null && <p className="text-sm text-zinc-500 text-center py-8">A carregar…</p>}
      {items && items.length === 0 && (
        <div data-testid="mp-empty" className="text-center py-10 text-zinc-500 text-sm">
          Ainda não há produtos publicados{category ? ` em ${category}` : ""}.<br />
          <Link to="/vender" className="text-[#c8102e] font-semibold underline">Sê o primeiro a vender</Link>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 mb-8">{items?.map(p => <ProductCard key={p.id} p={p} />)}</div>
    </div>
  );
}

function Quick({ to, icon: I, label, testid, active, highlight }) {
  return (
    <Link to={to} data-testid={testid} className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border text-[11px] font-semibold transition-colors
      ${highlight ? "bg-black text-white border-black hover:bg-[#c8102e]" : active ? "bg-[#c8102e] text-white border-[#c8102e]" : "bg-white border-zinc-200 hover:border-black"}`}>
      <I size={16} /> {label}
    </Link>
  );
}
